import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import FormField from "components/molecules/FormField/FormField";
import { createShipmentTemplate } from "api";
import { isValueExist } from "helpers/validationHelpers";
import errorToast from "helpers/errorToast";

type saveTemplateProps = {
  isOpen: boolean;
  close: () => void;
  shipmentId: string;
};

const SaveTemplateDialog: React.FC<saveTemplateProps> = function ({ isOpen, close, shipmentId }) {
  const methods = useForm({
    shouldFocusError: true,
  });
  const { handleSubmit, reset } = methods;
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = (data: { [key: string]: string }) => {
    setIsLoading(true);
    const params = {
      reference_number: shipmentId,
      label: data.template_name,
    };
    createShipmentTemplate(params)
      .then((res) => {
        if (res.status === 201)
          toast(
            intl.formatMessage({
              id: "components.molecules.navigation.saveTemplateSuccess",
              defaultMessage: "Template has been saved successfully",
            }),
            {
              type: "success",
            },
          );
      })
      .catch((err) => errorToast(err));
    reset();
    close();
  };

  return (
    <Dialog
      size="md"
      show={isOpen}
      title={intl.formatMessage({
        id: "general.saveTemplate",
        defaultMessage: "Save template",
      })}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form>
          <div className="mb-3">
            <FormField
              id="id_template_name"
              field_id="template_name"
              label={intl.formatMessage({
                id: "shipment-quotes.components.saveTemplateDialog.templateName",
                defaultMessage: "Template name",
              })}
              placeholder={intl.formatMessage({
                id: "shipment-quotes.components.saveTemplateDialog.templateName",
                defaultMessage: "Template name",
              })}
              mandatory
              type="text"
              eventContext="Save Template Dialog"
              validations={{
                validate: { valueExist: isValueExist },
              }}
            />
          </div>
          <div className="d-flex">
            <Button
              variant="outline-primary"
              size="lg"
              onClick={close}
              label={intl.formatMessage({
                id: "general.cancel",
                defaultMessage: "Cancel",
              })}
            />
            <Button
              size="lg"
              onClick={handleSubmit((data) => onSubmit(data))}
              className="ml-auto"
              isLoading={isLoading}
              label={intl.formatMessage({
                id: "general.save",
                defaultMessage: "Save",
              })}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};

SaveTemplateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  shipmentId: PropTypes.string.isRequired,
};

export default SaveTemplateDialog;
